import axios from "axios";
import { API_URL } from "./constants";
import { jwtDecode } from "jwt-decode";
import { isIamEndpoint, forceIamReplay, captureIamFailure, newRequestId } from "./iamObservability";

// Short-circuit requests whose URL path ends with '/null' or '/undefined'.
// These come from components that render before a required id has loaded (e.g. lawFirmId),
// and always 500 on the backend with "expected a number but got 'null'". Returning []
// matches what callers expect when there are no results and avoids log noise.
const API_TIMEOUT_MS = 30000;

const _pathEndsWithNullish = (endpoint) =>
  typeof endpoint === "string" && /\/(null|undefined)\/?(\?|$)/.test(endpoint);

export const apiClient = async (endpoint, method = "GET", data = {}, params = {}, headers = {}) => {
  if (_pathEndsWithNullish(endpoint)) {
    // eslint-disable-next-line no-console
    console.warn(`apiClient: skipping ${method} ${endpoint} (nullish path segment)`);
    return [];
  }
  // Record the full IAM session for replay (warnings don't trip on-error replay).
  const isIam = isIamEndpoint(endpoint);
  // Correlation id for IAM requests only (those POSTs are already preflighted, so
  // the X-Request-ID header adds no new preflight). Ties FE event ↔ BE event ↔ api_logs.
  const requestId = isIam ? newRequestId() : null;
  if (isIam) forceIamReplay();
  let token = localStorage.getItem("accessToken");

  // Helper function to refresh token
  const refreshTokenFunc = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token available");

      const response = await axios.post(`${API_URL}/token/refresh`, {
        refresh: refreshToken,
      });

      const newToken = response.data.access;
      localStorage.setItem("accessToken", newToken);
      return newToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      captureIamFailure("/token/refresh", error?.response?.status || 401, error?.response?.data);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/";
      throw error;
    }
  };

  // Pre-check: if token is expired, try to refresh
  if (token) {
    const { exp } = jwtDecode(token);
    if (exp * 1000 < Date.now()) {
      try {
        token = await refreshTokenFunc();
      } catch (err) {
        return; // Redirect handled in refreshTokenFunc
      }
    }
  }

  const makeRequest = async (tokenToUse) => {
    const requestHeaders = {
      ...headers,
    };
    if (requestId) {
      requestHeaders["X-Request-ID"] = requestId;
    }
    if (tokenToUse) {
      requestHeaders.Authorization = `Bearer ${tokenToUse}`;
    }

    return await axios({
      url: `${API_URL}${endpoint}`,
      method,
      data,
      params,
      headers: requestHeaders,
      timeout: API_TIMEOUT_MS,
    });
  };

  try {
    const response = await makeRequest(token);
    return response.data;
  } catch (error) {
    console.error("API call error:", error);
    // Surface IAM auth/registration failures (handled non-2xx) in Sentry.
    if (error.response && isIam) {
      captureIamFailure(endpoint, error.response.status, error.response.data, requestId);
    }
    if (error.response && error.response.status === 401) {
      // Access token might have just expired or been revoked
      try {
        const newToken = await refreshTokenFunc();
        const retryResponse = await makeRequest(newToken);
        return retryResponse.data;
      } catch (refreshError) {
        throw error.response ? error.response.data : new Error("API call failed after refresh attempt");
      }
    }
    throw error.response ? error.response.data : new Error("API call failed");
  }
};

// Returns the full axios response (status, headers, data). Useful when callers
// need to branch on HTTP status codes like 202 vs 200.
export const apiClientRaw = async (endpoint, method = "GET", data = {}, params = {}, headers = {}) => {
  if (_pathEndsWithNullish(endpoint)) {
    // eslint-disable-next-line no-console
    console.warn(`apiClientRaw: skipping ${method} ${endpoint} (nullish path segment)`);
    return { data: [], status: 204 };
  }
  // Record the full IAM session for replay (warnings don't trip on-error replay).
  const isIam = isIamEndpoint(endpoint);
  const requestId = isIam ? newRequestId() : null;
  if (isIam) forceIamReplay();
  let token = localStorage.getItem("accessToken");

  // Helper function to refresh token
  const refreshTokenFunc = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token available");

      const response = await axios.post(`${API_URL}/token/refresh`, {
        refresh: refreshToken,
      });

      const newToken = response.data.access;
      localStorage.setItem("accessToken", newToken);
      return newToken;
    } catch (error) {
      captureIamFailure("/token/refresh", error?.response?.status || 401, error?.response?.data);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/";
      throw error;
    }
  };

  if (token) {
    const { exp } = jwtDecode(token);
    if (exp * 1000 < Date.now()) {
      try {
        token = await refreshTokenFunc();
      } catch (err) {
        return;
      }
    }
  }

  const makeRequest = async (tokenToUse) => {
    const requestHeaders = {
      ...headers,
    };
    if (requestId) {
      requestHeaders["X-Request-ID"] = requestId;
    }
    if (tokenToUse) {
      requestHeaders.Authorization = `Bearer ${tokenToUse}`;
    }

    return await axios({
      url: `${API_URL}${endpoint}`,
      method,
      data,
      params,
      headers: requestHeaders,
      validateStatus: () => true,
      timeout: API_TIMEOUT_MS,
    });
  };

  try {
    let response = await makeRequest(token);

    if (response.status === 401) {
      try {
        const newToken = await refreshTokenFunc();
        response = await makeRequest(newToken);
      } catch (refreshError) {
        // Refresh failed, response will be the original or handled by redirect
      }
    }

    // Surface IAM auth/registration failures (apiClientRaw returns non-2xx, no throw).
    if (isIam && response && response.status >= 400) {
      captureIamFailure(endpoint, response.status, response.data, requestId);
    }

    return response;
  } catch (error) {
    console.error("API raw call error:", error);
    if (isIam && error?.response) {
      captureIamFailure(endpoint, error.response.status, error.response.data, requestId);
    }
    throw error;
  }
};

export const uploadGIA = async (file) => {
  try {
    const token = localStorage.getItem("accessToken");

    // Check if token is expired
    if (token) {
      const { exp } = jwtDecode(token);
      if (exp * 1000 < Date.now()) {
        localStorage.removeItem("accessToken");
        window.location.href = "/";
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(`${API_URL}/upload-gia/`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // DON'T set 'Content-Type' here; browser sets it with boundary
      },
    });

    return response.data;
  } catch (error) {
    console.error("GIA upload error:", error);
    throw error.response?.data || new Error("GIA upload failed");
  }
};


export const uploadLOA = async (file) => {
  try {
    const token = localStorage.getItem("accessToken");

    // Check if token is expired
    if (token) {
      const { exp } = jwtDecode(token);
      if (exp * 1000 < Date.now()) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("lastVisitedPath");
        window.location.href = "/";
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(`${API_URL}/upload-loa/`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // DON'T set 'Content-Type' here; browser sets it with boundary
      },
    });

    return response.data;
  } catch (error) {
    console.error("GIA upload error:", error);
    throw error.response?.data || new Error("GIA upload failed");
  }
};